import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, defer, from, TimeoutError } from 'rxjs';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import { LoadingController } from '@ionic/angular';
import { ToastService } from './services/toast/toast.service';
import { LanguageService } from './services/language.service';
import { AuthService } from './services/auth/auth.service';
import { ConnectivityService } from './services/connectivity.service';
import { SILENT_REQUEST } from './http-context-tokens';
import { environment } from '../environments/environment';

/** Milliseconds before a request is considered timed out */
const REQUEST_TIMEOUT_MS = 30_000;

export { SILENT_REQUEST };

@Injectable()
export class HttpTimeoutInterceptor implements HttpInterceptor {
  private readonly MAX_RETRIES = 3;
  private static readonly PUBLIC_AUTH_PATHS = [
    'party/auth/login',
    'party/auth/register',
    'party/auth/credentialManagement',
    'party/auth/verify/otp',
    'party/auth/webauthn/login',
    'party/auth/deviceKey/login',
  ];

  constructor(
    private toastService: ToastService,
    private loadingController: LoadingController,
    private languageService: LanguageService,
    private authService: AuthService,
    private connectivityService: ConnectivityService
  ) { }

  private async dismissActiveLoader(): Promise<void> {
    const loader = await this.loadingController.getTop();
    if (loader) { await loader.dismiss(); }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const silent = request.context.get(SILENT_REQUEST);

    return from(this.attachToken(request)).pipe(
      switchMap(updatedRequest => this.handleWithRetry(updatedRequest, next, 0)),
      catchError(async (err) => {
        await this.dismissActiveLoader();

        if (err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403) && HttpTimeoutInterceptor.endsSession(request)) {
          await this.authService.logout401();
          throw err;
        }

        if (silent) {
          throw err;
        }

        if (err instanceof TimeoutError) {
          this.toastService.presentToast(
            'Connection Timeout',
            'The request is taking too long. Please check your internet connection and try again.',
            'bottom', 'warning', 5000
          );
          throw err;
        }

        if (err instanceof HttpErrorResponse) {
          if (err.status === 0) {
            const failure = await this.connectivityService.classifyFailure();
            if (failure === 'offline') {
              this.toastService.presentToast(
                'No Connection',
                'Unable to reach the server. Please check your internet connection.',
                'bottom', 'warning', 5000
              );
            } else if (failure === 'service-down') {
              this.toastService.presentToast(
                'Service Unavailable',
                "Our systems are temporarily down — this isn't your connection. Please try again shortly.",
                'bottom', 'danger', 5000
              );
            } else {
              this.toastService.presentToast(
                'Connection Problem',
                'Unable to reach the server right now. Please try again in a moment.',
                'bottom', 'warning', 5000
              );
            }
          } else if (err.status >= 500) {
            this.toastService.presentToast(
              'Server Error',
              'Something went wrong on our end. Please try again in a moment.',
              'bottom', 'danger', 5000
            );
          }
        }

        throw err;
      })
    );
  }

  static endsSession(request: HttpRequest<unknown>): boolean {
    if (!request.url.startsWith(environment.reactiveServer)) {
      return false;
    }
    const path = request.url.substring(environment.reactiveServer.length);
    return !HttpTimeoutInterceptor.PUBLIC_AUTH_PATHS.some((publicPath) => path.startsWith(publicPath));
  }

  static isApiRequest(request: HttpRequest<unknown>): boolean {
    return [environment.reactiveServer, environment.apiServer].some(base => !!base && request.url.startsWith(base));
  }

  private async attachToken(request: HttpRequest<any>): Promise<HttpRequest<any>> {
    const headersConfig: { [name: string]: string | string[] } = {};

    request.headers.keys().forEach(key => {
      headersConfig[key] = request.headers.getAll(key) as string[];
    });

    headersConfig['Content-Type'] = 'application/json';
    headersConfig['Accept-Language'] = this.languageService.currentLanguage;
    if (HttpTimeoutInterceptor.isApiRequest(request)) {
      headersConfig['X-Requested-With'] = 'XMLHttpRequest';
    }

    const newHeaders = new HttpHeaders(headersConfig);
    return request.clone({ headers: newHeaders });
  }

  private handleWithRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
    retryCount: number
  ): Observable<HttpEvent<any>> {
    return defer(() =>
      next.handle(request).pipe(
        timeout(REQUEST_TIMEOUT_MS),
        catchError(err => {
          // Retry only when the server is genuinely unreachable or overloaded:
          //   status 0   — no network / CORS / connection refused
          //   502 / 503  — gateway/service temporarily unavailable
          //   TimeoutError — request exceeded REQUEST_TIMEOUT_MS
          // All other status codes (4xx, 500, 504, …) are not retried.
          const isRetryable =
            err instanceof TimeoutError ||
            (err instanceof HttpErrorResponse &&
              (err.status === 0 || err.status === 502 || err.status === 503));

          if (isRetryable && retryCount < this.MAX_RETRIES) {
            return this.handleWithRetry(request, next, retryCount + 1);
          }

          return throwError(() => err);
        })
      )
    );
  }
}
